import React from "react";
import { useState, useCallback } from 'react';
import { Loader2, Construction } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { useAdspySearch } from '@/hooks/useAdspySearch';
import { FilterBar } from '@/components/adspy/FilterBar';
import { AdCard } from '@/components/adspy/AdCard';
import { AdDetailModal } from '@/components/adspy/AdDetailModal';
import type { SearchFilters, AdWithBrand } from '@/types/adspy';

// FEATURE FLAG - AdSpy temporarily disabled
// TODO: Re-enable when asset proxying/storage is implemented
const ENABLE_AD_SPY = false;

const defaultFilters: SearchFilters = {
  searchTerm: '',
  format: 'ALL',
  platform: 'ALL',
  status: 'ALL',
  industry: 'ALL',
  language: 'ALL',
  dateStart: null,
  dateEnd: null,
  minRunningDays: null,
  maxRunningDays: null,
};

export default function Discover() {
  const [filters, setFilters] = useState<SearchFilters>(defaultFilters);
  const [selectedAd, setSelectedAd] = useState<AdWithBrand | null>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useAdspySearch({ 
    filters,
    enabled: ENABLE_AD_SPY,
  });

  const { ref: loadMoreRef } = useInView({
    onChange: (inView) => {
      if (!ENABLE_AD_SPY) return;
      if (inView && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
  });

  const handleFilterChange = useCallback((newFilters: Partial<SearchFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const allAds = data?.pages.flatMap((page) => page.ads) || [];

  if (!ENABLE_AD_SPY) {
    return (
      <div className="flex flex-col h-full">
        <header className="border-b border-border bg-card p-4">
          <h1 className="text-2xl font-bold text-foreground mb-4">Discover Ads</h1>
        </header>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-8 text-center max-w-md">
            <Construction className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-amber-200 mb-2">
              Feature Under Maintenance
            </h2>
            <p className="text-amber-300/80">
              The Discover Ads feature is temporarily unavailable while we implement improved media handling. Check back soon!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="border-b border-border bg-card p-4">
        <h1 className="text-2xl font-bold text-foreground mb-4">Discover Ads</h1>
        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : allAds.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <p className="text-lg">No ads found</p>
            <p className="text-sm">Try adjusting your search filters</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {allAds.map((ad) => (
                <AdCard 
                  key={ad.id} 
                  ad={ad as AdWithBrand} 
                  onExpand={(a) => setSelectedAd(a)} 
                />
              ))}
            </div>
            
            {/* Load more trigger */}
            {hasNextPage && (
              <div ref={loadMoreRef} className="flex justify-center py-8">
                {isFetchingNextPage && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
              </div>
            )}
          </>
        )}
      </div>

      <AdDetailModal
        ad={selectedAd}
        open={!!selectedAd}
        onOpenChange={(open) => !open && setSelectedAd(null)}
      />
    </div>
  );
}
